import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  ro: {
    // Navigation
    home: 'Acasă',
    apartments: 'Apartamente',
    about: 'Despre Noi',
    contact: 'Contact',
    policies: 'Politici',
    admin: 'Admin',
    
    // Homepage
    hero_title: 'Apartamente de închiriat în Iași',
    hero_subtitle: 'Descoperiți apartamente elegante în inima Moldovei',
    view_apartments: 'Vezi Apartamentele',
    
    // Apartments
    available_apartments: 'Apartamente Disponibile',
    apartment_details: 'Detalii Apartament',
    filters: 'Filtre',
    property_type: 'Tip Proprietate',
    area: 'Zonă',
    features: 'Caracteristici',
    all: 'Toate',
    price: 'Preț',
    status: 'Status',
    available: 'Disponibil',
    occupied: 'Ocupat',
    view_details: 'Vezi Detalii',
    share: 'Distribui',
    book_now: 'Rezervă Acum',
    
    // Contact
    contact_us: 'Contactează-ne',
    name: 'Nume',
    email: 'Email',
    subject: 'Subiect',
    message: 'Mesaj',
    send_message: 'Trimite Mesaj',
    visit_note: 'Vizitele se fac doar cu programare',
    
    // Newsletter
    subscribe_newsletter: 'Abonează-te la Newsletter',
    email_placeholder: 'Adresa ta de email',
    subscribe: 'Abonează-te',
    
    // Footer
    footer_text: '© 2025 RENTIASI.RO - Toate drepturile rezervate',
    
    // Admin
    dashboard: 'Tablou de Bord',
    total_apartments: 'Total Apartamente',
    login: 'Autentificare',
    logout: 'Deconectare',
    username: 'Utilizator',
    password: 'Parolă',
    add_apartment: 'Adaugă Apartament',
    edit_apartment: 'Editează Apartament',
    delete: 'Șterge',
    save: 'Salvează',
    cancel: 'Anulează',
    settings: 'Setări',
    newsletter: 'Newsletter',
    subscribers: 'Abonați',
    messages: 'Mesaje',
    categories: 'Categorii',
  },
  en: {
    // Navigation
    home: 'Home',
    apartments: 'Apartments',
    about: 'About',
    contact: 'Contact',
    policies: 'Policies',
    admin: 'Admin',
    
    // Homepage
    hero_title: 'Apartments for Rent in Iași',
    hero_subtitle: 'Discover elegant apartments in the heart of Moldavia',
    view_apartments: 'View Apartments',
    
    // Apartments
    available_apartments: 'Available Apartments',
    apartment_details: 'Apartment Details',
    filters: 'Filters',
    property_type: 'Property Type',
    area: 'Area',
    features: 'Features',
    all: 'All',
    price: 'Price',
    status: 'Status',
    available: 'Available',
    occupied: 'Occupied',
    view_details: 'View Details',
    share: 'Share',
    book_now: 'Book Now',
    
    // Contact
    contact_us: 'Contact Us',
    name: 'Name',
    email: 'Email',
    subject: 'Subject',
    message: 'Message',
    send_message: 'Send Message',
    visit_note: 'Visits by appointment only',
    
    // Newsletter
    subscribe_newsletter: 'Subscribe to Newsletter',
    email_placeholder: 'Your email address',
    subscribe: 'Subscribe',
    
    // Footer
    footer_text: '© 2025 RENTIASI.RO - All rights reserved',
    
    // Admin
    dashboard: 'Dashboard',
    total_apartments: 'Total Apartments',
    login: 'Login',
    logout: 'Logout',
    username: 'Username',
    password: 'Password',
    add_apartment: 'Add Apartment',
    edit_apartment: 'Edit Apartment',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    settings: 'Settings',
    newsletter: 'Newsletter',
    subscribers: 'Subscribers',
    messages: 'Messages',
    categories: 'Categories',
  },
  fr: {
    // Navigation
    home: 'Accueil',
    apartments: 'Appartements',
    about: 'À Propos',
    contact: 'Contact',
    policies: 'Politiques',
    admin: 'Admin',
    
    // Homepage
    hero_title: 'Appartements à louer à Iași',
    hero_subtitle: 'Découvrez des appartements élégants au cœur de la Moldavie',
    view_apartments: 'Voir les Appartements',
    
    // Apartments
    available_apartments: 'Appartements Disponibles',
    apartment_details: 'Détails de l\'Appartement',
    filters: 'Filtres',
    property_type: 'Type de Propriété',
    area: 'Zone',
    features: 'Caractéristiques',
    all: 'Tous',
    price: 'Prix',
    status: 'Statut',
    available: 'Disponible',
    occupied: 'Occupé',
    view_details: 'Voir Détails',
    share: 'Partager',
    book_now: 'Réserver',
    
    // Contact
    contact_us: 'Nous Contacter',
    name: 'Nom',
    email: 'Email',
    subject: 'Sujet',
    message: 'Message',
    send_message: 'Envoyer le Message',
    visit_note: 'Visites sur rendez-vous uniquement',
    
    // Newsletter
    subscribe_newsletter: 'S\'abonner à la Newsletter',
    email_placeholder: 'Votre adresse email',
    subscribe: 'S\'abonner',
    
    // Footer
    footer_text: '© 2025 RENTIASI.RO - Tous droits réservés',
    
    // Admin
    dashboard: 'Tableau de Bord',
    total_apartments: 'Total Appartements',
    login: 'Connexion',
    logout: 'Déconnexion',
    username: 'Utilisateur',
    password: 'Mot de passe',
    add_apartment: 'Ajouter Appartement',
    edit_apartment: 'Éditer Appartement',
    delete: 'Supprimer',
    save: 'Sauvegarder',
    cancel: 'Annuler',
    settings: 'Paramètres',
    newsletter: 'Newsletter',
    subscribers: 'Abonnés',
    messages: 'Messages',
    categories: 'Catégories',
  },
};

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [currency, setCurrency] = useState('EUR');
  const [exchangeRate, setExchangeRate] = useState(5.0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      setIsAuthenticated(true);
      setUser(JSON.parse(savedUser));
    }
    setAuthLoading(false);
  }, []);

  const t = (key) => {
    return translations[language]?.[key] || key;
  };

  const login = (token, userData) => {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      Authorization: `Bearer ${token}`,
    };
  };

  const convertPrice = (priceEur, priceRon) => {
    if (currency === 'EUR') {
      return `€${priceEur.toFixed(0)}`;
    }
    return `${priceRon.toFixed(0)} RON`;
  };

  const value = {
    language,
    setLanguage,
    currency,
    setCurrency,
    exchangeRate,
    setExchangeRate,
    t,
    isAuthenticated,
    user,
    login,
    logout,
    getAuthHeaders,
    convertPrice,
    authLoading,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};